import _, {isEqual, isString, uniqBy} from 'lodash';
import React, {FC, useState} from 'react';
import {connect} from 'react-redux';

import {
  isUploadedAttachmentModel,
  UploadedAttachmentModel,
} from '../../../../../libs/shared/core/src/models/api/attachmentModel';
import {ResolveStateProps} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {CommentModel} from '../../../../models/api/commentModel';
import {findCommentComposer} from '../../../../services/composers';
import {AppState} from '../../../../store/appState';
import {
  PublicSaveCommentOptions,
  saveCommentAsyncAction,
} from '../../../../store/data/comments/saveCommentActions';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getAnnotations, getConversations} from '../../../../store/data/dataSelectors';
import {getMentionableAliases} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {editCommentAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {getEmojiValidationMap} from '../../../../store/stateless/emojiSelectors';
import {EditCommentComposer} from './editCommentComposer';

/*
 * Props.
 */

interface EditCommentComposerContainerPublicProps {
  panelId: ViewerPanelId;
  comment: CommentModel;
  conversationId: number;
  onDeleteClick: () => void;
  cellsContext: CellsContext;
}

type EditCommentComposerStateProps = ResolveStateProps<typeof mapStateToProps>;
type EditCommentComposerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type EditCommentComposerContainerProps = EditCommentComposerContainerPublicProps &
  EditCommentComposerStateProps &
  EditCommentComposerDispatchProps;

/*
 * Selectors.
 */

function getConversationId(_state: AppState, props: EditCommentComposerContainerPublicProps) {
  return props.conversationId;
}
function getComment(_state: AppState, props: EditCommentComposerContainerPublicProps) {
  return props.comment;
}

function makeGetConversation() {
  return createSelector([getConversationId, getConversations], (conversationId, conversations) => {
    const conversation = conversations[conversationId];
    if (!conversation) {
      throw new Error(`A conversation could not be found for the following id: ${conversationId}.`);
    }

    return conversation;
  });
}

function makeGetAnnotation() {
  return createSelector([getComment, getAnnotations], (comment, annotations) =>
    comment.annotationUid ? annotations[comment.annotationUid] : undefined,
  );
}

/*
 * Component.
 */

const EditCommentComposerContainerComponent: FC<EditCommentComposerContainerProps> = (props) => {
  const {
    comment,
    company,
    conversation,
    editComment,
    mentionableAliases,
    panelId,
    saveCommentAsync,
    user,
    emojiValidationMap,
  } = props;
  const commentAttachments = _(comment.attachments).filter(isUploadedAttachmentModel).compact().value();
  const [attachments, setAttachments] = useState(commentAttachments);
  const [referencedActivityId, setReferencedActivityId] = useState(comment.referencedActivityId);
  const [annotation, setAnnotation] = useState(props.annotation);

  const endCommentEdit = () => {
    // Clears out the editingCommentUid on the Redux state, which will unmount this component
    editComment(panelId, undefined);

    const composer = findCommentComposer(comment.conversationId);
    if (composer) {
      composer.focus();
    }
  };

  const addAttachment = (attachment: UploadedAttachmentModel) => {
    setAttachments((previousAttachments) => uniqBy([...previousAttachments, attachment], (a) => a.uid));
  };

  const removeAttachment = (cid: string) => {
    setAttachments((previousAttachments) =>
      uniqBy(
        previousAttachments.filter((a) => a.cid !== cid),
        (a) => a.cid,
      ),
    );
  };

  const saveEditedComment = (options: PublicSaveCommentOptions) => {
    // Find if some attachments have changed.
    const commentAttachmentsCids = commentAttachments.map((a) => a.cid);
    const stateAttachmentsCids = attachments.map((a) => a.cid);

    const didAttachmentsChange = !isEqual(stateAttachmentsCids, commentAttachmentsCids);
    const didTextChange = isString(options.newText) && options.newText !== comment.text;
    const didReferencedActivityChange = comment.referencedActivityId !== referencedActivityId;

    // Check if we actually need to save or not.
    if (!didAttachmentsChange && !didTextChange && !didReferencedActivityChange) {
      return;
    }

    // We need to do a bit of computation to send the right options to `saveCommentAsync`
    const removedAttachmentCids = _(commentAttachments)
      .difference(attachments)
      .map((a) => a.cid)
      .value();

    const addedAttachments = _(attachments).difference(commentAttachments).value();

    saveCommentAsync(comment, {
      ...options,
      newReferencedActivityId: referencedActivityId || null,
      newAnnotation: annotation || null,
      panelId,
      removedAttachmentCids: (removedAttachmentCids.length > 0 && removedAttachmentCids) || undefined,
      addedAttachments: (addedAttachments.length > 0 && addedAttachments) || undefined,
    });
  };

  const onTextChange = (newText: string) => {
    const options = {newText};
    endCommentEdit();
    saveEditedComment(options);
  };

  // The only update that's supported while editing is removing the referenced activity
  const updateReferencedActivity = (newActivityId: number | null) => {
    if (!newActivityId) {
      setReferencedActivityId(undefined);
      setAnnotation(undefined);
    }
  };

  return (
    <EditCommentComposer
      panelId={panelId}
      addAttachment={addAttachment}
      attachments={attachments}
      company={company}
      user={user}
      conversation={conversation}
      comment={comment}
      endCommentEdit={endCommentEdit}
      mentionableAliases={mentionableAliases}
      onRemoveAttachmentClick={removeAttachment}
      onTextChange={onTextChange}
      referencedActivityId={referencedActivityId}
      updateReferencedActivity={updateReferencedActivity}
      annotation={annotation}
      onDeleteClick={props.onDeleteClick}
      emojiValidationMap={emojiValidationMap}
      cellsContext={props.cellsContext}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<EditCommentComposerContainerPublicProps>()(() => ({
  company: getCompany,
  conversation: makeGetConversation(),
  mentionableAliases: getMentionableAliases,
  user: getUser,
  emojiValidationMap: getEmojiValidationMap,
  annotation: makeGetAnnotation(),
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  editComment: editCommentAction,
  saveCommentAsync: saveCommentAsyncAction,
}));

export const EditCommentComposerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditCommentComposerContainerComponent);
